import React from 'react';
import { useSelector } from 'react-redux';

import { Row, Col } from '@zillow/zrs-comet';
import { Spacer } from '@zillow/constellation';

import format from 'date-fns/format';

import accessUtils from 'app/shared/utils/accessUtils';

import PropertyConstants from 'app/shared/constants/PropertyConstants';

import { DetailedDescription } from 'app/shared/components/postingPath/DetailedDescription';
import AmenitiesAndRules from 'app/shared/components/postingPath/AmenitiesAndRules';
import ContactInformation from 'app/shared/components/postingPath/ContactInformation';
import EnableApplicationsRadio from 'app/shared/components/postingPath/EnableApplicationsRadio';
import HousingConnector from 'app/shared/components/postingPath/HousingConnector';
import LeadGuaranteeBanner from 'app/shared/components/postingPath/LeadGuaranteeBanner';
import ListingDetails from 'app/shared/components/postingPath/ListingDetails';
import ListingStatusBanner from 'app/shared/components/postingPath/ListingStatusBanner';
import Media from 'app/shared/components/postingPath/Media';
import Scheduler from 'app/shared/components/postingPath/Scheduler';
import { AppSelectors, UserSelectors, PropertiesSelectors } from 'app/shared/selectors';
import ListingActivationTerms from 'app/shared/components/ListingActivationTerms';
import RenewListing from './components/renewListing';


const { SYNDICATION_OVERVIEW_STATUS } = PropertyConstants;

const SingleFamilyForm = ({
    canUpdateApplications,
    contactNameRef,
    contactPhoneRef,
    descriptionRef,
    errorObj,
    floorplansRef,
    formData,
    forRentByRef,
    immediateUpdateAndSave,
    isApplicationsAcceptedRef,
    isInputFormDisabled,
    isListingPhotoProcessing,
    isPaymentValid,
    leaseDurationRef,
    rentPriceRef,
    saveFloorplanData,
    savePropertyData,
    setFailedRenewalData,
    setSuccessfulRenewalData,
    statusMessage,
    updateErrorObj,
    updateFormData,
    updateListingPhotoProcessingStatus,
    updateOnFocus,
    updateStatusMessage,
}) => {
    const { appConfig, cookieEnabledServices } = useSelector(state => AppSelectors.makeApp(state));
    const { leadGuarantee, expirationDate } = useSelector(state => PropertiesSelectors.makeCurrentProperty(state));
    const user = useSelector(state => UserSelectors.makeUser(state));
    const { isSeattleHousingConnector, isEnterpriseCustomer } = user;

    // TODO: remove after we fully release feature
    const canAccessFlatFee = accessUtils.canAccessFlatFee({ appConfig, cookieEnabledServices });

    const isPaymentRequired = Boolean(formData.paidInclusion && formData.paidInclusion.isPaymentRequired);
    const {
        isEligible: isEligibleForLeadGuarantee,
    } = leadGuarantee;

    const {
        seattleHousingConnectorStatus,
        isApplicationsAccepted,
        syndication,
        paidInclusion = {},
    } = formData;

    const { isEnabled: isListingInNucleusMarket } = paidInclusion;
    const isFreelistingInPaidMarket =
        isListingInNucleusMarket &&
        formData.active === true &&
        isPaymentValid === false &&
        isPaymentRequired === false;

    const shouldDisplayRenewListing = isFreelistingInPaidMarket && !formData.restrictions.isUpdateListingRestricted;

    // lead guarantee
    const renderLeadGuaranteeBanner = () => {
        if (formData.active) {
            const formattedDateToExpire = expirationDate ? format(expirationDate, 'MMM DD') : '-';
            return (
                <LeadGuaranteeBanner
                    heading="Lead Guarantee"
                    body={`Your renewal will be free if you don’t get any leads by ${formattedDateToExpire}`}
                    actionLabel="Check out our help article on getting more renter interest"
                    actionPath="https://help.zillowrentalmanager.com/hc/en-us/articles/360048462014"
                    actionTarget="_blank"
                />
            );
        }
        if (!formData.active && !formData.archived) {
            const body = `If you don't receive a lead within a week,
                it's free to list until you do (for up to 8 weeks).`;
            return (
                <LeadGuaranteeBanner
                    heading="Backed by our Lead Guarantee"
                    body={body}
                    actionLabel="Explore Lead Guarante"
                    actionPath="https://help.zillowrentalmanager.com/hc/en-us/articles/360048462014"
                    actionTarget="_blank"
                />
            );
        }
        return null;
    };

    return (
        <Row>
            <Col xs={0} lg={2} />
            <Col xs={12} lg={8}>
                {shouldDisplayRenewListing && (
                    <RenewListing
                        formData={formData}
                        updateStatusMessage={updateStatusMessage}
                        setSuccessfulRenewalData={setSuccessfulRenewalData}
                        setFailedRenewalData={setFailedRenewalData}
                    />
                )}
                {(syndication.status !== SYNDICATION_OVERVIEW_STATUS.INACTIVE) && (
                    <ListingStatusBanner universal={syndication.universal} />
                )}
                {!canAccessFlatFee && isEligibleForLeadGuarantee && (
                    <Spacer marginBottom="md">
                        {renderLeadGuaranteeBanner()}
                    </Spacer>
                )}
                <ListingDetails
                    descriptionRef={descriptionRef}
                    errorObj={errorObj}
                    floorplansRef={floorplansRef}
                    formData={formData}
                    immediateUpdateAndSave={immediateUpdateAndSave}
                    isEnterpriseCustomer={isEnterpriseCustomer}
                    isInputFormDisabled={isInputFormDisabled}
                    leaseDurationRef={leaseDurationRef}
                    rentPriceRef={rentPriceRef}
                    saveFloorplanData={saveFloorplanData}
                    savePropertyData={savePropertyData}
                    updateErrorObj={updateErrorObj}
                    updateFormData={updateFormData}
                    updateOnFocus={updateOnFocus}
                />
                <AmenitiesAndRules
                    formData={formData}
                    errorObj={errorObj}
                    updateErrorObj={updateErrorObj}
                    immediateUpdateAndSave={immediateUpdateAndSave}
                    updateFormData={updateFormData}
                    updateStatusMessage={updateStatusMessage}
                    isInputFormDisabled={isInputFormDisabled}
                />
                <DetailedDescription
                    descriptionRef={descriptionRef}
                    errorObj={errorObj}
                    formData={formData}
                    isInputFormDisabled={isInputFormDisabled}
                    savePropertyData={savePropertyData}
                    updateFormData={updateFormData}
                    updateOnFocus={updateOnFocus}
                />
                <Media
                    errorObj={errorObj}
                    formData={formData}
                    hasError={Boolean(errorObj.photos)}
                    isInputFormDisabled={isInputFormDisabled}
                    isListingPhotoProcessing={isListingPhotoProcessing}
                    statusMessage={statusMessage}
                    tours={[formData.tours[0]]}
                    tourType="singleFamily"
                    updateErrorObj={updateErrorObj}
                    updateFormData={updateFormData}
                    updateListingPhotoProcessingStatus={updateListingPhotoProcessingStatus}
                    updateStatusMessage={updateStatusMessage}
                />
                <Spacer marginY="md" />
                <ContactInformation
                    contactNameRef={contactNameRef}
                    contactPhoneRef={contactPhoneRef}
                    errorObj={errorObj}
                    formData={formData}
                    forRentByRef={forRentByRef}
                    immediateUpdateAndSave={immediateUpdateAndSave}
                    isInputFormDisabled={isInputFormDisabled}
                    isPaymentRequired={isPaymentRequired}
                    savePropertyData={savePropertyData}
                    updateErrorObj={updateErrorObj}
                    updateFormData={updateFormData}
                    updateOnFocus={updateOnFocus}
                    updateStatusMessage={updateStatusMessage}
                />
                <Scheduler
                    isInputFormDisabled={isInputFormDisabled}
                    schedule={formData.schedule}
                    updateStatusMessage={updateStatusMessage}
                    updateFormData={updateFormData}
                    listingAlias={formData.listingAlias}
                />
                {isSeattleHousingConnector && (
                    <HousingConnector
                        initSelectedOption={seattleHousingConnectorStatus}
                        handleSelectChange={immediateUpdateAndSave}
                    />
                )}
                {canUpdateApplications && (
                    <EnableApplicationsRadio
                        isApplicationsAcceptedRef={isApplicationsAcceptedRef}
                        errorObj={errorObj}
                        updateErrorObj={updateErrorObj}
                        isApplicationsAccepted={isApplicationsAccepted}
                        handleSelectionChange={immediateUpdateAndSave}
                    />
                )}
                <ListingActivationTerms
                    textCenter={true}
                    actionText={`By clicking ${formData.active ? 'Deactivate' : 'Publish'} listing`}
                />
            </Col>
            <Col xs={0} lg={2} />
        </Row>
    );
};

export default SingleFamilyForm;
